import {useEffect, useState} from 'react';
import {Alert, Button, Col, Input, Row, Spin, Typography, message} from 'antd';
import {useHistory, useParams} from 'react-router-dom';
import {NewGrade} from '@PikaDoApp/npm-pikado-logic';
import {GradesService} from './GradesService';
import {getCurrentAuthUserId} from '../../utils';

const gradesService = new GradesService();

export const GradeCreateEdit = () => {
  const history = useHistory();
  const {Title, Text} = Typography;
  const params: {gradeId?: string} = useParams();

  const isEditMode = !!params.gradeId;

  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [grade, setGrade] = useState<NewGrade | null>(null);
  const [gradeName, setGradeName] = useState<string>('');
  const [arabicGradeName, setArabicGradeName] = useState<string>('');

  useEffect(() => {
    if (!isEditMode) return;

    const fetchGrade = async () => {
      setIsLoadingData(true);
      setIsError(false);
      try {
        const fetchedGrade = await gradesService.getNewGradeById(
          params.gradeId!,
        );
        if (fetchedGrade) {
          setGrade(fetchedGrade);
          setGradeName(fetchedGrade.name || '');
          setArabicGradeName(fetchedGrade.arabicName || '');
        } else {
          setIsError(true);
          message.error('Grade not found');
        }
      } catch (error) {
        console.error('Error loading grade:', error);
        setIsError(true);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchGrade();
  }, [isEditMode, params.gradeId]);

  const handleSubmit = async () => {
    if (!gradeName || gradeName.trim() === '') {
      message.error('Please enter a grade name');
      return;
    }

    setIsSaving(true);

    try {
      if (isEditMode) {
        const updateInput = {
          id: params.gradeId!,
          name: gradeName.trim(),
          arabicName: arabicGradeName.trim(),
        };
        await gradesService.updateNewGrade(updateInput);
        message.success('Grade updated successfully!');
      } else {
        const gradeInput = {
          name: gradeName.trim(),
          arabicName: arabicGradeName.trim(),
          createdBy: getCurrentAuthUserId(),
        };
        await gradesService.createNewGrade(gradeInput);
        message.success('Grade created successfully!');
      }
      history.push('/grades');
    } catch (error) {
      console.error(
        `Error ${isEditMode ? 'updating' : 'creating'} grade:`,
        error,
      );
      message.error(`Failed to ${isEditMode ? 'update' : 'create'} grade.`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{padding: 10}}>
      <Title style={{textAlign: 'center'}}>
        {isEditMode ? 'Edit Grade' : 'Create Grade'}
      </Title>

      {isLoadingData && (
        <div style={{textAlign: 'center', padding: '50px 0'}}>
          <Spin size="large" tip="Loading grade..." />
        </div>
      )}

      {isError && (
        <Alert
          message="Error"
          description="Failed to load grade. Please try again."
          type="error"
          showIcon
          style={{marginBottom: 20}}
        />
      )}

      {(!isEditMode || (!isLoadingData && !isError && grade)) && (
        <Spin
          spinning={isSaving}
          tip={isEditMode ? 'Updating grade...' : 'Creating grade...'}>
          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Grade Name *
                </Text>
                <Input
                  placeholder="Enter Grade Name"
                  onChange={e => setGradeName(e.target.value)}
                  value={gradeName}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Arabic Grade Name
                </Text>
                <Input
                  placeholder="Enter Arabic Grade Name"
                  onChange={e => setArabicGradeName(e.target.value)}
                  value={arabicGradeName}
                />
              </div>
            </Col>
          </Row>

          <div style={{margin: 20}}>
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={isSaving}
              disabled={isSaving}>
              {isEditMode ? 'Update Grade' : 'Create Grade'}
            </Button>
            <Button
              style={{marginLeft: 10}}
              onClick={() => history.push('/grades')}
              disabled={isSaving}>
              Cancel
            </Button>
          </div>
        </Spin>
      )}
    </div>
  );
};
